/**
 * Validator Status Checker
 *
 * Checks validator deposit status by querying:
 * 1. Beacon chain first (source of truth for validator status)
 * 2. Execution layer second (deposit contract events)
 *
 * This mirrors the logic from the Python deposit monitor:
 * - BEACON CHAIN FIRST: If validator exists → show beacon status (active, pending, etc.)
 * - EXECUTION LAYER: If deposit tx found → deposited, waiting for beacon chain
 * - NEITHER: Not deposited yet → allow user to confirm deposit
 */

import { BEACON_RPC_URL, RPC_URL, CONTRACT_ADDRESS } from './envVars';

// DepositEvent signature: keccak256("DepositEvent(bytes,bytes,bytes,bytes,bytes)")
const DEPOSIT_EVENT_SIGNATURE =
  '0x649bbc62d0e31342afea4e5cd82d4049e7e1ee912fc0889aa790803be39038c5';

// How many hours to scan back for deposits (matches Python monitor)
const LOOKBACK_HOURS = 24;
const BLOCK_TIME = 10; // PulseChain block time in seconds

/**
 * Beacon chain validator status from the Beacon API
 */
export type BeaconValidatorStatus =
  | 'pending_initialized'
  | 'pending_queued'
  | 'active_ongoing'
  | 'active_exiting'
  | 'active_slashed'
  | 'exited_unslashed'
  | 'exited_slashed'
  | 'withdrawal_possible'
  | 'withdrawal_done';

/**
 * Combined status for display
 */
export enum ValidatorDepositStatus {
  NOT_DEPOSITED = 'NOT_DEPOSITED', // No deposit found on chain
  DEPOSITED_WAITING = 'DEPOSITED_WAITING', // Deposit tx found, waiting for beacon chain (12-18 hours)
  PENDING_INIT = 'PENDING_INIT', // On beacon chain, pending initialization
  PENDING_QUEUED = 'PENDING_QUEUED', // On beacon chain, in activation queue
  ACTIVE = 'ACTIVE', // Validator is active on beacon chain
  EXITING = 'EXITING', // Validator is exiting
  EXITED = 'EXITED', // Validator has exited
  SLASHED = 'SLASHED', // Validator was slashed
  WITHDRAWN = 'WITHDRAWN', // Validator withdrawal complete
  ERROR = 'ERROR', // Error checking status
}

export interface ValidatorStatusResult {
  status: ValidatorDepositStatus;
  beaconStatus: BeaconValidatorStatus | null;
  beaconIndex: number | null;
  beaconBalance: string | null;
  execDeposited: boolean;
  execTxHash: string | null;
  execBlockNumber: number | null;
  execDepositIndex: number | null;
  error: string | null;
}

export interface ValidatorStatusConfig {
  beaconRpcUrl?: string;
  executionRpcUrl?: string;
  depositContractAddress?: string;
  signal?: AbortSignal;
}

/**
 * Result from checking a validator on the beacon chain
 */
interface BeaconValidatorResult {
  found: boolean;
  status: BeaconValidatorStatus | null;
  index: number | null;
  balance: string | null;
}

/**
 * Valid beacon validator statuses for type guard
 */
const VALID_BEACON_STATUSES: readonly BeaconValidatorStatus[] = [
  'pending_initialized',
  'pending_queued',
  'active_ongoing',
  'active_exiting',
  'active_slashed',
  'exited_unslashed',
  'exited_slashed',
  'withdrawal_possible',
  'withdrawal_done',
] as const;

/**
 * Type guard to validate beacon validator status from API response
 */
function isBeaconValidatorStatus(
  value: unknown
): value is BeaconValidatorStatus {
  return (
    typeof value === 'string' &&
    VALID_BEACON_STATUSES.includes(value as BeaconValidatorStatus)
  );
}

/**
 * JSON-RPC response structure for block number
 */
interface JsonRpcBlockNumberResponse {
  jsonrpc: string;
  id: number;
  result?: string;
  error?: { code: number; message: string };
}

/**
 * JSON-RPC response structure for getLogs
 */
interface JsonRpcLogsResponse {
  jsonrpc: string;
  id: number;
  result?: Array<{
    data: string;
    transactionHash: string;
    blockNumber: string;
  }>;
  error?: { code: number; message: string };
}

/**
 * Beacon API validator response structure
 */
interface BeaconValidatorApiResponse {
  data?: {
    index?: string;
    status?: string;
    validator?: {
      pubkey?: string;
      effective_balance?: string;
    };
  };
}

/**
 * Beacon API batch validators response structure
 */
interface BeaconValidatorsBatchApiResponse {
  data?: Array<{
    index?: string;
    status?: string;
    validator?: {
      pubkey?: string;
      effective_balance?: string;
    };
  }>;
}

/**
 * Get the latest block number from the execution layer
 */
async function getLatestBlock(
  rpcUrl: string,
  signal?: AbortSignal
): Promise<number | null> {
  try {
    const response = await fetch(rpcUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        jsonrpc: '2.0',
        method: 'eth_blockNumber',
        params: [],
        id: 1,
      }),
      signal,
    });

    if (!response.ok) return null;

    const json: JsonRpcBlockNumberResponse = await response.json();
    if (!json.result || json.error) return null;

    return parseInt(json.result, 16);
  } catch {
    return null;
  }
}

/**
 * Parse deposit event data to extract pubkey and other fields
 */
function parseDepositEvent(log: {
  data: string;
  transactionHash: string;
  blockNumber: string;
}): {
  pubkey: string;
  depositIndex: number;
  txHash: string;
  blockNumber: number;
} | null {
  const data = log.data.toLowerCase().replace('0x', '');

  if (data.length < 320) return null;

  try {
    // Get offsets to each field (5 dynamic fields with 32-byte offsets each)
    const pubkeyOffset = parseInt(data.slice(0, 64), 16) * 2;
    const indexOffset = parseInt(data.slice(256, 320), 16) * 2;

    // Extract pubkey (skip length, read 48 bytes = 96 hex chars)
    const pubkeyDataPos = pubkeyOffset + 64;
    const pubkey = data.slice(pubkeyDataPos, pubkeyDataPos + 96);

    // Extract index (8 bytes little-endian)
    const indexDataPos = indexOffset + 64;
    const indexHex = data.slice(indexDataPos, indexDataPos + 16);
    const indexBytes = indexHex.match(/.{2}/g);
    if (!indexBytes) return null;

    const depositIndex = indexBytes.reduce(
      (acc, byte, i) => acc + parseInt(byte, 16) * Math.pow(256, i),
      0
    );

    return {
      pubkey,
      depositIndex,
      txHash: log.transactionHash,
      blockNumber: parseInt(log.blockNumber, 16),
    };
  } catch {
    return null;
  }
}

/**
 * Query deposit events for specific pubkeys from the execution layer
 */
async function queryDepositEvents(
  rpcUrl: string,
  contractAddress: string,
  fromBlock: number,
  toBlock: number,
  signal?: AbortSignal
): Promise<
  Array<{
    data: string;
    transactionHash: string;
    blockNumber: string;
  }>
> {
  try {
    const response = await fetch(rpcUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        jsonrpc: '2.0',
        method: 'eth_getLogs',
        params: [
          {
            address: contractAddress,
            fromBlock: `0x${fromBlock.toString(16)}`,
            toBlock: `0x${toBlock.toString(16)}`,
            topics: [DEPOSIT_EVENT_SIGNATURE],
          },
        ],
        id: 1,
      }),
      signal,
    });

    if (!response.ok) return [];

    const json: JsonRpcLogsResponse = await response.json();
    if (json.error || !json.result) return [];

    return json.result;
  } catch {
    return [];
  }
}

/**
 * Check if a validator exists on the beacon chain
 */
async function checkBeaconValidator(
  beaconRpcUrl: string,
  pubkey: string,
  signal?: AbortSignal
): Promise<BeaconValidatorResult> {
  const notFound: BeaconValidatorResult = {
    found: false,
    status: null,
    index: null,
    balance: null,
  };

  try {
    const normalizedPubkey = pubkey.startsWith('0x') ? pubkey : `0x${pubkey}`;
    const response = await fetch(
      `${beaconRpcUrl}/eth/v1/beacon/states/head/validators/${normalizedPubkey}`,
      { signal }
    );

    if (response.status === 404 || !response.ok) {
      return notFound;
    }

    const json: BeaconValidatorApiResponse = await response.json();
    const validatorData = json.data;

    if (
      !validatorData?.index ||
      !isBeaconValidatorStatus(validatorData.status)
    ) {
      return notFound;
    }

    return {
      found: true,
      status: validatorData.status,
      index: parseInt(validatorData.index, 10),
      balance: validatorData.validator?.effective_balance ?? null,
    };
  } catch {
    return notFound;
  }
}

/**
 * Batch check multiple validators on beacon chain using POST endpoint
 */
async function checkBeaconValidatorsBatch(
  beaconRpcUrl: string,
  pubkeys: string[],
  signal?: AbortSignal
): Promise<Map<string, BeaconValidatorResult>> {
  const results = new Map<string, BeaconValidatorResult>();
  const notFound: BeaconValidatorResult = {
    found: false,
    status: null,
    index: null,
    balance: null,
  };

  if (pubkeys.length === 0) return results;

  const fallbackToIndividual = async (): Promise<Map<
    string,
    BeaconValidatorResult
  >> => {
    for (const pk of pubkeys) {
      const result = await checkBeaconValidator(beaconRpcUrl, pk, signal);
      results.set(pk.toLowerCase().replace('0x', ''), result);
    }
    return results;
  };

  try {
    // Format pubkeys with 0x prefix
    const formattedPubkeys = pubkeys.map(pk =>
      pk.startsWith('0x') ? pk : `0x${pk}`
    );

    const response = await fetch(
      `${beaconRpcUrl}/eth/v1/beacon/states/head/validators`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: formattedPubkeys }),
        signal,
      }
    );

    if (!response.ok) {
      return fallbackToIndividual();
    }

    const json: BeaconValidatorsBatchApiResponse = await response.json();
    const validatorsData = json.data ?? [];

    // Map results by pubkey
    for (const v of validatorsData) {
      const pk = (v.validator?.pubkey ?? '').toLowerCase().replace('0x', '');
      if (pk && v.index && isBeaconValidatorStatus(v.status)) {
        results.set(pk, {
          found: true,
          status: v.status,
          index: parseInt(v.index, 10),
          balance: v.validator?.effective_balance ?? null,
        });
      }
    }

    // Mark missing pubkeys as not found
    for (const pk of pubkeys) {
      const pkClean = pk.toLowerCase().replace('0x', '');
      if (!results.has(pkClean)) {
        results.set(pkClean, notFound);
      }
    }

    return results;
  } catch {
    return fallbackToIndividual();
  }
}

/**
 * Map beacon chain status to our display status
 */
function mapBeaconStatusToDisplayStatus(
  beaconStatus: BeaconValidatorStatus
): ValidatorDepositStatus {
  if (beaconStatus === 'pending_initialized') {
    return ValidatorDepositStatus.PENDING_INIT;
  }
  if (beaconStatus === 'pending_queued') {
    return ValidatorDepositStatus.PENDING_QUEUED;
  }
  if (beaconStatus === 'active_ongoing') {
    return ValidatorDepositStatus.ACTIVE;
  }
  if (beaconStatus === 'active_exiting') {
    return ValidatorDepositStatus.EXITING;
  }
  if (beaconStatus === 'active_slashed') {
    return ValidatorDepositStatus.SLASHED;
  }
  if (beaconStatus === 'exited_unslashed') {
    return ValidatorDepositStatus.EXITED;
  }
  if (beaconStatus === 'exited_slashed') {
    return ValidatorDepositStatus.SLASHED;
  }
  if (beaconStatus === 'withdrawal_possible') {
    return ValidatorDepositStatus.EXITED;
  }
  if (beaconStatus === 'withdrawal_done') {
    return ValidatorDepositStatus.WITHDRAWN;
  }
  return ValidatorDepositStatus.ERROR;
}

/**
 * Check the status of a single validator
 *
 * Order of checks (matching Python monitor):
 * 1. Beacon chain first - source of truth
 * 2. Execution layer - deposit contract events
 */
export async function checkValidatorStatus(
  pubkey: string,
  config: ValidatorStatusConfig = {}
): Promise<ValidatorStatusResult> {
  const {
    beaconRpcUrl = BEACON_RPC_URL,
    executionRpcUrl = RPC_URL,
    depositContractAddress = CONTRACT_ADDRESS,
    signal,
  } = config;

  const normalizedPubkey = pubkey.toLowerCase().replace('0x', '');

  // Step 1: Check beacon chain first
  const beaconResult = await checkBeaconValidator(
    beaconRpcUrl,
    normalizedPubkey,
    signal
  );

  if (beaconResult.found && beaconResult.status) {
    return {
      status: mapBeaconStatusToDisplayStatus(beaconResult.status),
      beaconStatus: beaconResult.status,
      beaconIndex: beaconResult.index,
      beaconBalance: beaconResult.balance,
      execDeposited: true, // If on beacon chain, deposit must have happened
      execTxHash: null,
      execBlockNumber: null,
      execDepositIndex: null,
      error: null,
    };
  }

  // Step 2: Check execution layer for deposit events
  const latestBlock = await getLatestBlock(executionRpcUrl, signal);
  if (latestBlock === null) {
    return {
      status: ValidatorDepositStatus.ERROR,
      beaconStatus: null,
      beaconIndex: null,
      beaconBalance: null,
      execDeposited: false,
      execTxHash: null,
      execBlockNumber: null,
      execDepositIndex: null,
      error: 'Unable to get latest block',
    };
  }

  // Calculate lookback range
  const blocksPerHour = Math.floor(3600 / BLOCK_TIME);
  const lookbackBlocks = blocksPerHour * LOOKBACK_HOURS;
  const fromBlock = Math.max(0, latestBlock - lookbackBlocks);

  // Query deposit events in chunks to avoid RPC limits
  const chunkSize = 10000;
  let depositFound: {
    pubkey: string;
    depositIndex: number;
    txHash: string;
    blockNumber: number;
  } | null = null;

  for (
    let scanFrom = fromBlock;
    scanFrom < latestBlock;
    scanFrom += chunkSize
  ) {
    const scanTo = Math.min(scanFrom + chunkSize, latestBlock);
    const logs = await queryDepositEvents(
      executionRpcUrl,
      depositContractAddress,
      scanFrom,
      scanTo,
      signal
    );

    for (const log of logs) {
      const parsed = parseDepositEvent(log);
      if (parsed && parsed.pubkey.toLowerCase() === normalizedPubkey) {
        depositFound = parsed;
        break;
      }
    }

    if (depositFound) break;
  }

  if (depositFound) {
    return {
      status: ValidatorDepositStatus.DEPOSITED_WAITING,
      beaconStatus: null,
      beaconIndex: null,
      beaconBalance: null,
      execDeposited: true,
      execTxHash: depositFound.txHash,
      execBlockNumber: depositFound.blockNumber,
      execDepositIndex: depositFound.depositIndex,
      error: null,
    };
  }

  // Step 3: Not found anywhere - not deposited
  return {
    status: ValidatorDepositStatus.NOT_DEPOSITED,
    beaconStatus: null,
    beaconIndex: null,
    beaconBalance: null,
    execDeposited: false,
    execTxHash: null,
    execBlockNumber: null,
    execDepositIndex: null,
    error: null,
  };
}

/**
 * Check status of multiple validators in batch
 */
export async function checkValidatorsStatus(
  pubkeys: string[],
  config: ValidatorStatusConfig = {}
): Promise<Map<string, ValidatorStatusResult>> {
  const {
    beaconRpcUrl = BEACON_RPC_URL,
    executionRpcUrl = RPC_URL,
    depositContractAddress = CONTRACT_ADDRESS,
    signal,
  } = config;

  const results = new Map<string, ValidatorStatusResult>();
  const normalizedPubkeys = pubkeys.map(pk =>
    pk.toLowerCase().replace('0x', '')
  );

  // Step 1: Batch check beacon chain
  const beaconResults = await checkBeaconValidatorsBatch(
    beaconRpcUrl,
    normalizedPubkeys,
    signal
  );

  // Track which pubkeys still need execution layer check
  const needExecCheck: string[] = [];

  for (const pk of normalizedPubkeys) {
    const beaconResult = beaconResults.get(pk);
    if (beaconResult?.found && beaconResult.status) {
      results.set(pk, {
        status: mapBeaconStatusToDisplayStatus(beaconResult.status),
        beaconStatus: beaconResult.status,
        beaconIndex: beaconResult.index,
        beaconBalance: beaconResult.balance,
        execDeposited: true,
        execTxHash: null,
        execBlockNumber: null,
        execDepositIndex: null,
        error: null,
      });
    } else {
      needExecCheck.push(pk);
    }
  }

  // Step 2: Check execution layer for remaining pubkeys
  if (needExecCheck.length > 0) {
    const latestBlock = await getLatestBlock(executionRpcUrl, signal);
    if (latestBlock === null) {
      // Mark all as error
      for (const pk of needExecCheck) {
        results.set(pk, {
          status: ValidatorDepositStatus.ERROR,
          beaconStatus: null,
          beaconIndex: null,
          beaconBalance: null,
          execDeposited: false,
          execTxHash: null,
          execBlockNumber: null,
          execDepositIndex: null,
          error: 'Unable to get latest block',
        });
      }
      return results;
    }

    // Calculate lookback range
    const blocksPerHour = Math.floor(3600 / BLOCK_TIME);
    const lookbackBlocks = blocksPerHour * LOOKBACK_HOURS;
    const fromBlock = Math.max(0, latestBlock - lookbackBlocks);

    // Create a set for fast lookup
    const needExecSet = new Set(needExecCheck);
    const depositsFound = new Map<
      string,
      {
        pubkey: string;
        depositIndex: number;
        txHash: string;
        blockNumber: number;
      }
    >();

    // Query deposit events in chunks
    const chunkSize = 10000;
    for (
      let scanFrom = fromBlock;
      scanFrom < latestBlock;
      scanFrom += chunkSize
    ) {
      const scanTo = Math.min(scanFrom + chunkSize, latestBlock);
      const logs = await queryDepositEvents(
        executionRpcUrl,
        depositContractAddress,
        scanFrom,
        scanTo,
        signal
      );

      for (const log of logs) {
        const parsed = parseDepositEvent(log);
        if (parsed && needExecSet.has(parsed.pubkey.toLowerCase())) {
          depositsFound.set(parsed.pubkey.toLowerCase(), parsed);
        }
      }
    }

    // Populate results for exec-checked pubkeys
    for (const pk of needExecCheck) {
      const deposit = depositsFound.get(pk);
      if (deposit) {
        results.set(pk, {
          status: ValidatorDepositStatus.DEPOSITED_WAITING,
          beaconStatus: null,
          beaconIndex: null,
          beaconBalance: null,
          execDeposited: true,
          execTxHash: deposit.txHash,
          execBlockNumber: deposit.blockNumber,
          execDepositIndex: deposit.depositIndex,
          error: null,
        });
      } else {
        results.set(pk, {
          status: ValidatorDepositStatus.NOT_DEPOSITED,
          beaconStatus: null,
          beaconIndex: null,
          beaconBalance: null,
          execDeposited: false,
          execTxHash: null,
          execBlockNumber: null,
          execDepositIndex: null,
          error: null,
        });
      }
    }
  }

  return results;
}
